import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
} from 'react';
import { turnstileSiteKey } from '@/config/turnstileEnv';

export type TurnstileFieldHandle = {
  ensureToken: () => Promise<string | null>;
  reset: () => void;
};

export type TurnstileFieldProps = {
  /** Contenedor del iframe (~300×65). Por defecto centrado a ancho completo. */
  className?: string;
};

function waitMs(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

function logTurnstile(msg: string, extra?: unknown): void {
  if (extra !== undefined) console.warn('[Turnstile]', msg, extra);
  else console.warn('[Turnstile]', msg);
}

/**
 * Turnstile visible (tamaño normal). En Cloudflare Turnstile usa un widget tipo **Managed**
 * para que el checkbox/reto aparezca cuando corresponda.
 */
const TurnstileField = forwardRef<TurnstileFieldHandle, TurnstileFieldProps>(
  function TurnstileField({ className }, ref) {
    const key = turnstileSiteKey();
    const innerRef = useRef<TurnstileInstance>(null);
    const tokenRef = useRef<string | null>(null);

    const onSuccess = useCallback((t: string) => {
      tokenRef.current = t;
    }, []);

    const onExpire = useCallback(() => {
      tokenRef.current = null;
    }, []);

    const onError = useCallback((code?: string) => {
      logTurnstile('error del widget (dominio / clave / red):', code);
      tokenRef.current = null;
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        async ensureToken() {
          if (!key) return null;
          if (tokenRef.current) return tokenRef.current;

          const deadline = Date.now() + 12000;
          let inst = innerRef.current;
          while (!inst && Date.now() < deadline) {
            await waitMs(40);
            inst = innerRef.current;
          }
          if (!inst) {
            logTurnstile(
              'el widget no está listo todavía; espera 1–2 s y reintenta, o revisa la Site Key y dominios en Cloudflare Turnstile.'
            );
            return null;
          }

          try {
            const t = await inst.getResponsePromise(25000);
            if (!t) {
              logTurnstile('token vacío tras getResponsePromise');
            }
            return t ?? null;
          } catch (e) {
            logTurnstile('getResponsePromise falló:', e);
            return null;
          }
        },
        reset() {
          tokenRef.current = null;
          innerRef.current?.reset();
        },
      }),
      [key]
    );

    if (!key) return null;

    return (
      <div
        className={
          className ?? 'flex w-full justify-center min-h-[65px] py-1'
        }
      >
        <div className="rounded-xl border border-[#E9D9F2] bg-white p-3 shadow-sm">
          <Turnstile
            ref={innerRef}
            siteKey={key}
            options={{
              size: 'normal',
              // Fijo claro: "auto" hereda modo oscuro del SO y el widget se ve negro.
              theme: 'light',
            }}
            onSuccess={onSuccess}
            onExpire={onExpire}
            onError={onError}
          />
        </div>
      </div>
    );
  }
);

export default TurnstileField;
